import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchReviews } from "src/redux/slices/reviews";

// КАРУСЕЛЬ ФОТО-ОТЗЫВОВ — ЛЕНТА В САМОМ НИЗУ СТРАНИЦЫ.
//
// Отзывы у нас двух видов: написанные на сайте (текст и звёзды) и картинки —
// скриншоты переписки в WhatsApp, сторис, фото из поездки. Читать длинную
// колонку текста внизу страницы человек не станет, а вот пролистать полосу
// живых кадров — станет: это последнее доказательство перед тем, как он
// нажмёт «оставить заявку».
//
// Поэтому здесь только картинки: из каждого отзыва берутся его фото, текст
// остаётся на странице отзывов. Если фото ни у одного отзыва нет, блок не
// рисуется вовсе — пустая карусель хуже её отсутствия.
//
// fetchOnMount: страница отзывов сама грузит отзывы, и второй запрос отсюда
// лишний (он ещё и очищает список на время загрузки — список мигал бы).
export default function ReviewsCarousel({ fetchOnMount = true, className = "" }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState(null);
  const [paused, setPaused] = useState(false);

  // Отзывы о конкретном туре живут на странице этого тура — здесь общие,
  // тот же отбор, что и в блоке отзывов на главной.
  const reviews = useSelector((state) =>
    state.reviews.reviews.items.filter((review) => !review.activityId)
  );

  useEffect(() => {
    if (fetchOnMount) dispatch(fetchReviews());
  }, [dispatch, fetchOnMount]);

  // Один кадр — один слайд: у отзыва с тремя фотографиями в ленте три
  // карточки, а не одна с мелкими плитками внутри.
  const photos = reviews.flatMap((review) =>
    (review.photos || []).map((url) => ({
      url,
      name: review.name,
      id: `${review._id}-${url}`,
    }))
  );

  const count = photos.length;

  // Прокрутка идёт по реальным координатам слайда, а не по «ширине контейнера
  // делить на два»: карточки разной ширины на телефоне и на десктопе, и
  // арифметика по контейнеру промахивалась бы мимо края.
  const scrollTo = useCallback((i) => {
    const track = trackRef.current;
    const slide = track?.children?.[i];
    if (!track || !slide) return;
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, []);

  const go = useCallback(
    (next) => {
      if (count === 0) return;
      const i = (next + count) % count;
      setIndex(i);
      scrollTo(i);
    },
    [count, scrollTo]
  );

  // Автопрокрутка: движущийся контент останавливается при наведении, при
  // фокусе с клавиатуры, при открытом полноэкранном кадре — и полностью
  // выключается для тех, кто просил ОС убрать анимации (WCAG 2.2.2).
  useEffect(() => {
    if (count <= 1 || paused || zoomed !== null) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => go(index + 1), 5000);
    return () => clearInterval(id);
  }, [count, paused, zoomed, index, go]);

  // Esc закрывает полноэкранный кадр: иначе с клавиатуры из него не выйти.
  useEffect(() => {
    if (zoomed === null) return;
    const onKey = (e) => e.key === "Escape" && setZoomed(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomed]);

  if (count === 0) return null;

  const src = (url) => `${process.env.REACT_APP_BASE_API}${url}`;

  return (
    <section
      aria-label={t("reviews.carouselTitle")}
      className={`w-full ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-kj-ink">
          <span className="underline underline-offset-[6px] decoration-kj-gold decoration-2">
            {t("reviews.carouselTitle")}
          </span>
        </h2>

        {count > 1 && (
          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label={t("reviews.prevPhoto")}
              className="h-10 w-10 rounded-full border border-kj-line text-kj-ink flex items-center justify-center hover:border-kj-gold hover:text-kj-gold transition-colors"
            >
              <ChevronLeft aria-hidden="true" className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label={t("reviews.nextPhoto")}
              className="h-10 w-10 rounded-full border border-kj-line text-kj-ink flex items-center justify-center hover:border-kj-gold hover:text-kj-gold transition-colors"
            >
              <ChevronRight aria-hidden="true" className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Лента листается и пальцем, и стрелками: snap-x доводит кадр до края,
          no-scrollbar убирает полосу прокрутки, но сама прокрутка остаётся —
          на тачпаде и на телефоне это привычнее кнопок. */}
      <ul
        ref={trackRef}
        className="no-scrollbar mt-6 flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2"
      >
        {photos.map((photo, i) => (
          <li
            key={photo.id}
            className="snap-start flex-shrink-0 w-[70%] sm:w-[38%] lg:w-[27%]"
          >
            <button
              type="button"
              onClick={() => setZoomed(i)}
              aria-label={t("reviews.openScreenshot")}
              className="block w-full rounded-2xl overflow-hidden border border-kj-line bg-kj-card/60 hover:border-kj-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold transition-colors"
            >
              {/* object-contain, а не cover: в ленте вперемешку вертикальные
                  скриншоты переписки и горизонтальные кадры из поездки.
                  Обрезка по общей высоте съедала бы у скриншота как раз
                  текст — то единственное, ради чего его и показывают.
                  Кадр вписывается целиком, поля закрашены фоном карточки. */}
              <span className="flex h-72 sm:h-80 w-full items-center justify-center bg-kj-dark/40">
                <img
                  src={src(photo.url)}
                  alt={t("reviews.screenshotAlt")}
                  loading="lazy"
                  className="max-h-full max-w-full object-contain"
                />
              </span>
              {photo.name && (
                <span className="block px-4 py-3 text-sm text-kj-sub truncate text-left">
                  {photo.name}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {zoomed !== null && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setZoomed(null)}
          role="presentation"
        >
          <button
            type="button"
            onClick={() => setZoomed(null)}
            aria-label={t("reviews.closePhoto")}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
          <img
            src={src(photos[zoomed].url)}
            alt={t("reviews.screenshotAlt")}
            className="max-h-full max-w-full object-contain rounded-lg"
          />
        </div>
      )}
    </section>
  );
}
